import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { X, Upload, Loader2, CheckSquare, Square, Wand2 } from 'lucide-react';
import { toast } from '../utils/toast';
const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CATEGORIES = [
  { key: 'cast_candidates', label: 'Cast' },
  { key: 'costume_candidates', label: 'Costumes' },
  { key: 'corner_candidates', label: 'Locations' },
];

/**
 * BulkIteratePanel
 *
 * Modal UI for the P0-3 Asset Bible Bulk-Iterate feature:
 *   - Pick a category (Cast/Costumes/Locations)
 *   - Multi-select existing assets
 *   - Type shared feedback
 *   - Optionally drop reference images (brand assets, fabric swatches, etc.)
 *   - Fire POST /projects/{id}/assets/bulk-iterate
 */
export default function BulkIteratePanel({ projectId, assetBible, open, onClose, onDone }) {
  const [category, setCategory] = useState('costume_candidates');
  const [selected, setSelected] = useState(new Set());
  const [feedback, setFeedback] = useState('');
  const [refFiles, setRefFiles] = useState([]); // File[]
  const [submitting, setSubmitting] = useState(false);

  const candidates = useMemo(() => {
    return (assetBible?.[category] || []).map((c, i) => ({ ...c, _idx: i }));
  }, [assetBible, category]);

  const toggle = (idx) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };

  const selectAll = () => {
    const all = new Set(candidates.filter(c => c.url).map(c => c._idx));
    setSelected(all);
  };

  const clearAll = () => setSelected(new Set());

  const onCategoryChange = (k) => {
    setCategory(k);
    setSelected(new Set());
  };

  const handleFiles = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const next = [...refFiles, ...files].slice(0, 10);
    setRefFiles(next);
    e.target.value = '';
  };

  const removeRef = (i) => setRefFiles(files => files.filter((_, idx) => idx !== i));

  const applyBulk = async () => {
    if (selected.size === 0) {
      toast.error('Select at least one asset');
      return;
    }
    if (!feedback.trim() && refFiles.length === 0) {
      toast.error('Add a shared prompt or drop reference images');
      return;
    }
    setSubmitting(true);
    try {
      // Step 1: upload reference files if any
      let reference_urls = [];
      if (refFiles.length > 0) {
        const form = new FormData();
        refFiles.forEach(f => form.append('files', f));
        const up = await axios.post(`${API}/projects/${projectId}/assets/bulk-upload-references`, form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        reference_urls = (up.data.urls || []).map(u => u.url);
      }
      // Step 2: fire bulk-iterate
      const res = await axios.post(`${API}/projects/${projectId}/assets/bulk-iterate`, {
        category,
        indexes: Array.from(selected),
        feedback: feedback.trim(),
        reference_urls,
      });
      toast.success(`Iterating ${res.data.queued} ${category.replace('_candidates', '')} — check tiles for progress`);
      onDone && onDone();
      onClose && onClose();
    } catch (e) {
      toast.error(`Bulk iterate failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="bulk-iterate-modal">
      <div className="w-full max-w-4xl max-h-[90vh] flex flex-col rounded-2xl border border-ai-border shadow-2xl" style={{ background: '#0a0f1a' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-ai-border">
          <div>
            <h2 className="text-base font-semibold text-white flex items-center gap-2">
              <Wand2 className="w-4 h-4 text-ai-accent" />
              Bulk Iterate Assets
            </h2>
            <p className="text-[11px] text-white/50 mt-0.5">Apply the same direction to multiple assets at once.</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10" data-testid="bulk-iterate-close">
            <X className="w-4 h-4 text-white/60" />
          </button>
        </div>

        {/* Category tabs */}
        <div className="flex items-center gap-1 px-5 pt-4 border-b border-ai-border">
          {CATEGORIES.map(c => {
            const cnt = (assetBible?.[c.key] || []).length;
            return (
              <button
                key={c.key}
                type="button"
                onClick={() => onCategoryChange(c.key)}
                className={`px-3 py-2 text-xs font-medium rounded-t-lg border-b-2 transition-colors ${category === c.key ? 'text-ai-accent border-ai-accent' : 'text-white/50 border-transparent hover:text-white/70'}`}
                data-testid={`bulk-cat-${c.key}`}
              >
                {c.label} <span className="font-mono opacity-70">({cnt})</span>
              </button>
            );
          })}
          <div className="flex-1" />
          <button
            type="button"
            onClick={selectAll}
            className="text-[11px] px-2 py-1 rounded bg-white/5 hover:bg-white/10 text-white/60"
            data-testid="bulk-select-all"
          >
            Select all
          </button>
          <button
            type="button"
            onClick={clearAll}
            className="text-[11px] px-2 py-1 rounded bg-white/5 hover:bg-white/10 text-white/60"
            data-testid="bulk-clear-all"
          >
            Clear
          </button>
        </div>

        {/* Body — two columns on desktop */}
        <div className="flex-1 overflow-hidden grid grid-cols-1 md:grid-cols-[1fr_340px] gap-0">
          {/* Left: candidate grid */}
          <div className="overflow-y-auto p-4 border-r border-ai-border" data-testid="bulk-candidates-grid">
            {candidates.length === 0 && (
              <div className="text-xs text-white/40 text-center py-10">No assets in this category yet.</div>
            )}
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {candidates.map(c => {
                const isSel = selected.has(c._idx);
                const disabled = !c.url;
                return (
                  <button
                    key={c.id || c._idx}
                    type="button"
                    onClick={() => !disabled && toggle(c._idx)}
                    disabled={disabled}
                    className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all ${isSel ? 'border-ai-accent shadow-lg shadow-ai-accent/40' : 'border-white/10 hover:border-white/30'} ${disabled ? 'opacity-30' : ''}`}
                    data-testid={`bulk-pick-${category}-${c._idx}`}
                    title={c.name || `#${c._idx + 1}`}
                  >
                    {c.url ? (
                      <img
                        src={c.url.startsWith('http') ? c.url : `${process.env.REACT_APP_BACKEND_URL}${c.url}`}
                        alt={c.name || ''}
                        className="w-full h-full object-cover"
                        crossOrigin="anonymous"
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[10px] text-white/30">no image</div>
                    )}
                    <div className={`absolute top-1 left-1 w-5 h-5 rounded flex items-center justify-center ${isSel ? 'bg-ai-accent text-white' : 'bg-black/60 text-white/40'}`}>
                      {isSel ? <CheckSquare className="w-3.5 h-3.5" /> : <Square className="w-3.5 h-3.5" />}
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 bg-black/70 px-1 py-0.5">
                      <span className="text-[9px] text-white truncate block">{c.name || `#${c._idx + 1}`}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Right: shared prompt + references */}
          <div className="overflow-y-auto p-4 space-y-4 bg-black/20">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-white/50 font-semibold">Shared Direction</label>
              <textarea
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                rows={6}
                placeholder="e.g. 'Restyle for a rainy night scene, keep the logo placement identical.'"
                className="mt-1 w-full px-3 py-2 rounded-lg bg-white/[0.04] border border-white/10 text-xs text-white placeholder:text-white/25 focus:outline-none focus:border-ai-accent"
                data-testid="bulk-feedback-textarea"
              />
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-wider text-white/50 font-semibold">Reference Images (optional)</label>
              <label className="mt-1 flex flex-col items-center gap-1 p-4 rounded-lg border border-dashed border-white/15 bg-white/[0.02] cursor-pointer hover:border-white/30 transition-colors">
                <Upload className="w-5 h-5 text-white/40" />
                <span className="text-[11px] text-white/60">Drop or click — up to 10 files</span>
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleFiles}
                  className="hidden"
                  data-testid="bulk-ref-file-input"
                />
              </label>
              {refFiles.length > 0 && (
                <div className="mt-2 space-y-1 max-h-36 overflow-y-auto">
                  {refFiles.map((f, i) => (
                    <div key={i} className="flex items-center justify-between px-2 py-1 rounded bg-white/5 text-[11px]">
                      <span className="truncate text-white/70">{f.name}</span>
                      <button type="button" onClick={() => removeRef(i)} className="text-white/40 hover:text-rose-400">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="pt-2 border-t border-white/10">
              <div className="text-[11px] text-white/60 mb-2">
                <span className="font-semibold text-white">{selected.size}</span> selected
              </div>
              <button
                type="button"
                disabled={submitting || selected.size === 0 || (!feedback.trim() && refFiles.length === 0)}
                onClick={applyBulk}
                className={`w-full px-4 py-2.5 rounded-full text-sm font-medium transition-all flex items-center justify-center gap-2 ${submitting || selected.size === 0 || (!feedback.trim() && refFiles.length === 0) ? 'bg-white/10 text-white/30 cursor-not-allowed' : 'bg-ai-accent text-white hover:bg-ai-accent/80 active:scale-95'}`}
                data-testid="bulk-iterate-apply"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
                Apply to {selected.size || '…'} asset{selected.size === 1 ? '' : 's'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
